import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, AlertTriangleIcon, UserXIcon } from 'lucide-react';
interface RemoveFromOrgModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (deleteAllData: boolean) => Promise<void>;
  educatorName: string;
}
export const RemoveFromOrgModal: React.FC<RemoveFromOrgModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  educatorName
}) => {
  const [dataOption, setDataOption] = useState<'keep' | 'delete'>('keep');
  const [confirmationText, setConfirmationText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isConfirmationValid = confirmationText.toLowerCase() === educatorName.toLowerCase();
  const handleConfirm = async () => {
    if (!isConfirmationValid) return;
    setIsSubmitting(true);
    try {
      await onConfirm(dataOption === 'delete');
      setDataOption('keep');
      setConfirmationText('');
      onClose();
    } catch (error) {
      console.error('Error removing educator from organization:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    if (!isSubmitting) {
      setDataOption('keep');
      setConfirmationText('');
      onClose();
    }
  };
  return <AnimatePresence data-id="element-2120">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} data-id="element-2121" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose} data-id="element-2122">
            <div className="bg-surface-elevated rounded-2xl shadow-2xl w-full max-w-md border border-custom max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2123">
              {/* Header */}
              <div className="flex items-start justify-between p-4 sm:p-6 border-b border-custom" data-id="element-2124">
                <div className="flex items-center gap-3" data-id="element-2125">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-2126">
                    <UserXIcon size={20} className="text-red-600" data-id="element-2127" />
                  </div>
                  <div className="min-w-0" data-id="element-2128">
                    <h2 className="text-lg sm:text-xl font-bold text-text-primary" data-id="element-2129">
                      Remove from Organization
                    </h2>
                    <p className="text-xs sm:text-sm text-text-secondary mt-0.5 truncate" data-id="element-2130">
                      {educatorName}
                    </p>
                  </div>
                </div>
                <button onClick={handleClose} disabled={isSubmitting} className="p-2 rounded-lg hover:bg-surface transition-colors disabled:opacity-50 flex-shrink-0" aria-label="Close modal" data-id="element-2131">
                  <XIcon size={20} className="text-text-secondary" data-id="element-2132" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-4" data-id="element-2133">
                {/* Warning */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-id="element-2134">
                  <div className="flex gap-2" data-id="element-2135">
                    <AlertTriangleIcon size={16} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-2136" />
                    <p className="text-xs text-red-800 leading-relaxed" data-id="element-2137">
                      This delinks the educator from this organization. They'll
                      lose access to organization resources but can still log in
                      to Ainstein.
                    </p>
                  </div>
                </div>

                {/* Data Options */}
                <div className="space-y-2" data-id="element-2138">
                  <p className="text-sm font-semibold text-text-primary" data-id="element-2139">
                    Organization data handling:
                  </p>

                  {/* Option 1: Keep Data */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-custom hover:border-[#00B75F] transition-colors cursor-pointer group" data-id="element-2140">
                    <input type="radio" name="dataOption" value="keep" checked={dataOption === 'keep'} onChange={() => setDataOption('keep')} disabled={isSubmitting} className="mt-0.5 w-4 h-4 text-[#00B75F] focus:ring-2 focus:ring-[#00B75F] cursor-pointer disabled:opacity-50" data-id="element-2141" />
                    <div className="flex-1 min-w-0" data-id="element-2142">
                      <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-2143">
                        <span className="text-sm font-semibold text-text-primary" data-id="element-2144">
                          Keep data
                        </span>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#00B75F] bg-opacity-10 text-[#00B75F]" data-id="element-2145">
                          Recommended
                        </span>
                      </div>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2146">
                        Preserves activity for reporting. Shows as "(Left
                        Organization)" in Overview.
                      </p>
                    </div>
                  </label>

                  {/* Option 2: Delete Data */}
                  <label className="flex items-start gap-3 p-3 rounded-lg border-2 border-custom hover:border-red-500 transition-colors cursor-pointer group" data-id="element-2147">
                    <input type="radio" name="dataOption" value="delete" checked={dataOption === 'delete'} onChange={() => setDataOption('delete')} disabled={isSubmitting} className="mt-0.5 w-4 h-4 text-red-600 focus:ring-2 focus:ring-red-500 cursor-pointer disabled:opacity-50" data-id="element-2148" />
                    <div className="flex-1 min-w-0" data-id="element-2149">
                      <span className="text-sm font-semibold text-text-primary block mb-1" data-id="element-2150">
                        Delete data
                      </span>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-2151">
                        Removes all activity from organization analytics.
                        Educator's personal reports unchanged.
                      </p>
                    </div>
                  </label>
                </div>

                {/* Confirmation Input */}
                <div data-id="element-2152">
                  <label htmlFor="confirmation-text" className="block text-sm font-semibold text-text-primary mb-2" data-id="element-2153">
                    Type{' '}
                    <span className="font-mono text-red-600" data-id="element-2154">
                      {educatorName}
                    </span>{' '}
                    to confirm
                  </label>
                  <input id="confirmation-text" type="text" value={confirmationText} onChange={e => setConfirmationText(e.target.value)} placeholder="Type name to confirm" disabled={isSubmitting} className="w-full px-3 py-2 bg-surface border-2 border-custom rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-text-primary placeholder-text-tertiary disabled:opacity-50" data-id="element-2155" />
                  {confirmationText && !isConfirmationValid && <p className="text-xs text-red-600 mt-1.5" data-id="element-2156">
                      Name doesn't match
                    </p>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-6 border-t border-custom bg-surface" data-id="element-2157">
                <button onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 border-2 border-custom text-text-primary rounded-lg font-semibold hover:bg-surface-elevated transition-colors disabled:opacity-50" data-id="element-2158">
                  Cancel
                </button>
                <button onClick={handleConfirm} disabled={!isConfirmationValid || isSubmitting} className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-2159">
                  {isSubmitting ? <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2160" />
                      Removing...
                    </> : <>
                      <UserXIcon size={16} data-id="element-2161" />
                      Remove from Org
                    </>}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};